import { type FC } from 'react'

import {
  Box,
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { HintTooltip } from '~/components/common/HintTooltip'

type Props = {
  main: string
  sub?: string
}

/**
 * 2 行積みのテーブルセル表示（`.cell-stack` パターン）。
 * 1 行目に主値（コード）、2 行目に補足値（名称）を表示し、長い場合は ellipsis +
 * title 属性で全文を確認できる。
 * 視覚形は `design/ui-feasibility/components/table.md` の `.cell-stack` を参照。
 *
 * [design 差異] 2026-08-28 デザイナー判断（一覧の BG セル = PR #6758 / #6699 と
 * 同基準）。名称を薄字にせず本文と同じ濃さにし、**主従は色ではなく太さで付ける**。
 *
 * 主値側に fontWeight 600 を足しているのがこの変更の要点。元は主値が
 * 14px / 400 / high-emphasis（td と body からの継承）、補足値が
 * 14px / 400 / mid-emphasis で、**主従が色 1 本だけで成立していた**。
 * そのため補足値を high-emphasis にすると大きさ・太さ・色がすべて同じ 2 行が
 * 縦に並び、どちらが主か分からなくなる。太さを足すことで、一覧側と同じ
 * 「コード = 14px/600 / 名称 = 14px/400」の主従関係に揃う。
 *
 * 行間 2px は据え置く。基準の 8px は横並びのコード↔名称の左右間隔で、
 * 行間に入れると 14px 2 行の行高が実質倍になり、10 列 + 操作列ある同タブの
 * 密度が別物になる。
 */
export const CellStack: FC<Props> = ({ main, sub }) => (
  <Box
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      lineHeight: 1.25,
      minWidth: 0,
    }}
  >
    <HintTooltip content={main}>
      <span
        style={{
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={main}
      >
        {main}
      </span>
    </HintTooltip>
    {sub !== undefined && (
      <HintTooltip content={sub}>
        <span
          style={{
            fontSize: fontSizes.md.value,
            color: LightModeSemanticColors.Text.highEmphasis.value,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={sub}
        >
          {sub}
        </span>
      </HintTooltip>
    )}
  </Box>
)
